import { trackEvent } from '@helpers/analytics';
import { isSSR } from '@helpers/ssr';
import {
  ChevronSmallIcon,
  Dialog,
  InfoNoFillIcon,
  Link,
  LinkTheme,
  Typography,
  TypographyType,
  useBreakpoint,
} from '@vivino/js-react-common-ui';
import {
  BasicVintage,
  Loader,
  Price,
  ValueForMoneyScoresFull,
  explorerUrl,
  getActiveExperimentVariant,
  getUrlSearchParams,
  t,
} from '@vivino/js-web-common';
import React, { useEffect, useRef, useState } from 'react';

import { getVintageValueForMoneyScores } from '@lib/api/vintage';

import VFMGauge from '@components/icons/VFMGauge';

import Explainer from './components/Explainer/Explainer';
import VFMBadge from './components/VFMBadge/VFMBadge';

import { scaleToRotation } from './helpers';
import styles from './vfmscale.module.scss';

const TRANSLATIONS = {
  thanks: 'common.thanks',
  tellUsMore: 'common.tell_us_more',
  learnMore: 'common.learn_more',
  calculationText: 'components.shared.vfm_scale.calculation_text',
  browseWines: 'components.shared.vfm_scale.browse_better_value_wines',
};

interface VFMScaleProps {
  price: Price;
  countryCode?: string;
  qprScores: ValueForMoneyScoresFull;
  vintage: BasicVintage & { wine?: { style?: { id: number } } };
}

const HIGHLIGHT_SURVEY_FORM_ID = '01HWZ36Z28QKSDH5324EW1PZ64';

const VFMScale = ({ vintage, price, countryCode, qprScores }: VFMScaleProps) => {
  const [scores, setScores] = useState<ValueForMoneyScoresFull | null>(qprScores);
  const [openSurvey, setOpenSurvey] = useState(false);
  const [openExplainer, setOpenExplainer] = useState(false);
  const [scoresError, setScoresError] = useState(false);

  const isFirstRender = useRef(true);

  const priceId = price.id;
  const vintageId = vintage.id;

  const { isMobile } = useBreakpoint();
  let utmSource, utmMedium;
  if (!isSSR) {
    const searchParams = getUrlSearchParams();
    utmSource = searchParams.get('utm_source');
    utmMedium = searchParams.get('utm_medium');
  }

  // If you think the property names are pure madness, you're not alone. The IOS forced us into this
  const trackingProps = {
    Price_id: priceId,
    UTMSource: utmSource,
    UTMMedium: utmMedium,
    Vintage_id: vintageId,
    Price_amount: price.amount,
    Value_score: scores?.value_score,
    Value_category_id: scores?.value_category?.id,
    Placement: globalThis.__PRELOADED_TRACKING_SCREEN_NAME__,
    Value_score_peer_group_wine_style_id: scores?.peer_group?.wine_style_id,
    VFMTestVariant: getActiveExperimentVariant('value_for_money_highlight')?.name,
    Value_score_statistics_vintage_count: scores?.peer_group?.statistics?.vintage_count,
    Value_score_price_band_from_to: `${scores?.peer_group?.price_band.from}-${scores?.peer_group?.price_band.to}`,
  };

  useEffect(() => {
    // Skip the first render. Don't fetch data on SSR
    if (qprScores && isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    getVintageValueForMoneyScores(vintageId, priceId)
      .then(({ qpr_scores }) => {
        setScores(qpr_scores);
      })
      .catch(() => {
        setScores(null);
        setScoresError(true);
      });
  }, [vintageId, priceId]);

  useEffect(() => {
    if (scores) {
      trackEvent({
        event: 'Value - Highlight - Show',
        props: trackingProps,
      });
    }
  }, [scores]);

  function handleOpenExplainer(event) {
    event.stopPropagation();
    setOpenExplainer(true);
    trackEvent({
      event: 'Value explainer - Screen - Show',
      props: trackingProps,
    });
  }

  function betterValueTracker(event) {
    event.stopPropagation();

    trackEvent({
      event: 'Value - Highlight - Action',
      props: {
        ...trackingProps,
        Action: 'Shop better value',
      },
    });
  }

  const valueCategory = scores?.value_category;
  const betterValueElsewhere = [1, 2].includes(valueCategory?.id);
  const barometerPosition = valueCategory?.barometer_position;
  const wineStyleId = vintage.wine?.style?.id || scores?.peer_group?.wine_style_id;

  const exploreLink = explorerUrl({
    countryCode,
    vcOnly: true,
    orderBy: 'vfm_scores',
    priceRangeMax: price.amount * 1.2,
    wineStyleIds: [wineStyleId],
  });

  if (scoresError || !scores) {
    return null;
  }

  return (
    <>
      <div className={styles.container} onClick={handleOpenExplainer}>
        <div className={styles.scaleContainer}>
          <div className={styles.gauge} style={{ display: 'block', width: '60px', height: '35px' }}>
            <VFMGauge
              rotation={scaleToRotation(barometerPosition)}
              unAvailable={!barometerPosition}
            />
          </div>
          <div className={styles.badgeContainer}>
            <VFMBadge text={valueCategory?.title} position={barometerPosition} />
            {!isMobile && (
              <div style={{ display: 'block', width: '24px', height: '24px' }}>
                <InfoNoFillIcon
                  data-testid="info-icon"
                  className={styles.infoIcon}
                  onClick={handleOpenExplainer}
                />
              </div>
            )}
          </div>
        </div>
        {scores && (
          <div className={styles.subtext}>
            <Typography type={TypographyType.BodySmall}>{valueCategory?.subtitle}</Typography>
            {isMobile && <ChevronSmallIcon className={styles.chevron} />}
          </div>
        )}
        {betterValueElsewhere && (
          <Link
            target="_blank"
            theme={LinkTheme.Monochrome}
            onClick={betterValueTracker}
            className={styles.browseWines}
            href={exploreLink}
          >
            <Typography type={TypographyType.LabelLarge}>{t(TRANSLATIONS.browseWines)}</Typography>
          </Link>
        )}
      </div>
      {!isSSR && (
        <Explainer
          price={price}
          vintage={vintage}
          qprScores={scores}
          open={openExplainer}
          trackingProps={trackingProps}
          onClose={() => setOpenExplainer(false)}
        />
      )}
      <Dialog
        dismissable
        open={openSurvey}
        onClose={() => setOpenSurvey(false)}
        fullScreen={isMobile}
      >
        <div
          id="typeform-highlight-survey"
          data-testid="typeform-highlight-survey"
          className={styles.highlightSurvey}
          data-tf-live={HIGHLIGHT_SURVEY_FORM_ID}
          data-tf-on-close="onTypeformClose"
          data-tf-inline-on-mobile="true"
          data-tf-hide-footer="true"
        >
          <Loader />
        </div>
      </Dialog>
    </>
  );
};

export default VFMScale;
